import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import Button from '../ui/Button';
import { TaskProject } from '../../types';
import { DEFAULT_COLUMNS, PROJECT_COLORS, PROJECT_STATUS_LABELS, ProjectStatus } from '../../utils/projectConstants';

interface ProjectModalProps {
  isOpen: boolean;
  project: TaskProject | null;
  onClose: () => void;
  onSave: (data: {
    name: string;
    description: string;
    color: string;
    status: ProjectStatus;
    status_columns: string[];
  }) => Promise<void>;
}

const ProjectModal: React.FC<ProjectModalProps> = ({ isOpen, project, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string>(PROJECT_COLORS[0]);
  const [status, setStatus] = useState<ProjectStatus>('active');
  const [columnsText, setColumnsText] = useState(DEFAULT_COLUMNS.join(', '));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (project) {
      setName(project.name);
      setDescription(project.description || '');
      setColor(project.color || PROJECT_COLORS[0]);
      setStatus((project.status as ProjectStatus) || 'active');
      setColumnsText((project.status_columns || DEFAULT_COLUMNS).join(', '));
    } else {
      setName('');
      setDescription('');
      setColor(PROJECT_COLORS[0]);
      setStatus('active');
      setColumnsText(DEFAULT_COLUMNS.join(', '));
    }
  }, [isOpen, project?.id]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const cols = columnsText.split(',').map(c => c.trim()).filter(Boolean);
    setSaving(true);
    try {
      await onSave({
        name: trimmed,
        description: description.trim(),
        color,
        status,
        status_columns: cols.length > 0 ? cols : [...DEFAULT_COLUMNS],
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={open => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-base font-semibold text-gray-900 dark:text-white">
              {project ? 'Editar proyecto' : 'Nuevo proyecto'}
            </Dialog.Title>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Nombre</label>
              <input value={name} onChange={e => setName(e.target.value)} className="w-full text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 outline-none focus:ring-1 focus:ring-indigo-400" placeholder="Nombre del proyecto" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Descripción</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="w-full text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 resize-none outline-none focus:ring-1 focus:ring-indigo-400" placeholder="Descripción corta..." />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 block">Color</label>
              <div className="flex flex-wrap gap-2 items-center">
                {PROJECT_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-6 h-6 rounded-full transition-transform ${color === c ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''}`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
                <label
                  className="relative w-8 h-8 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 cursor-pointer overflow-hidden hover:border-indigo-400"
                  title="Color personalizado"
                >
                  <input
                    type="color"
                    value={color.startsWith('#') && color.length >= 7 ? color : '#6366f1'}
                    onChange={e => setColor(e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <span
                    className="absolute inset-0.5 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                </label>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Estado</label>
              <select value={status} onChange={e => setStatus(e.target.value as ProjectStatus)} className="w-full text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 outline-none">
                {Object.entries(PROJECT_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Columnas (separadas por coma)</label>
              <input value={columnsText} onChange={e => setColumnsText(e.target.value)} className="w-full text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 outline-none focus:ring-1 focus:ring-indigo-400" />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button variant="secondary" onClick={onClose}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving || !name.trim()}>
              {saving ? 'Guardando...' : project ? 'Guardar' : 'Crear proyecto'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ProjectModal;
